import { useState, useEffect, useRef } from 'react'
import { fetchAdoptions, submitVolunteer, INITIAL_ADOPTIONS, getLocalData } from '../services/api'

const Adoption = ({ onOpenDonate }) => {
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false)
  const [isModalAnimating, setIsModalAnimating] = useState(false)
  const [selectedPet, setSelectedPet] = useState(null)

  // Drag scroll states
  const [isDown, setIsDown] = useState(false)
  const [startX, setStartX] = useState(0)
  const [scrollLeftVal, setScrollLeftVal] = useState(0)
  const [isDragging, setIsDragging] = useState(false)

  // Form states
  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', address: '',
    experience: 'no', household: 'apartment', hoursAlone: '0-2'
  })
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [formError, setFormError] = useState('')

  const [pets, setPets] = useState(() => {
    const cached = getLocalData('adoptions', INITIAL_ADOPTIONS)
    return Array.isArray(cached)
      ? cached.filter((p) => !p.status || p.status.toLowerCase() !== 'adopted')
      : []
  })
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    document.title = "Adopt a Pet in Kolar & Srinivaspur, Karnataka | Chetak Animal Welfare Trust (CAWT)"
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Adopt rescue puppies and indie dogs in Srinivaspur, Kolar, and Karnataka. Vaccinated, dewormed, healthy pets waiting for loving families at CAWT.')
    }
  }, [])

  useEffect(() => {
    fetchAdoptions()
      .then((data) => {
        if (data && Array.isArray(data)) {
          const available = data.filter(
            (p) => !p.status || p.status.toLowerCase() !== 'adopted'
          )
          setPets(available)
        }
      })
      .catch((err) => {
        console.warn('Error fetching adoptions:', err)
      })
  }, [])

  // Close on ESC & lock body scroll
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isApplyModalOpen) {
        handleCloseApply()
      }
    }
    if (isApplyModalOpen) {
      document.body.style.overflow = 'hidden'
      window.addEventListener('keydown', handleKeyDown)
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isApplyModalOpen])

  const handleOpenApply = (pet) => {
    setSelectedPet(pet)
    setIsApplyModalOpen(true)
    setFormSubmitted(false)
    setFormError('')
    // Trigger smooth slide up
    setTimeout(() => {
      setIsModalAnimating(true)
    }, 20)
  }

  const handleCloseApply = () => {
    setIsModalAnimating(false)
    // Wait for smooth slide down animation before unmounting
    setTimeout(() => {
      setIsApplyModalOpen(false)
      setSelectedPet(null)
    }, 320)
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.name || !formData.phone || !formData.email || !formData.address) {
      setFormError('Please fill in all required fields.')
      return
    }

    setFormError('')

    // Send application to Hostinger API / Admin database
    const petName = selectedPet ? selectedPet.name : 'Pet Adoption'
    const applicationPayload = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      area_of_interest: `Adoption: ${petName}`,
      message: `Address: ${formData.address} | Experience: ${formData.experience} | Household: ${formData.household} | Hours Alone: ${formData.hoursAlone}`,
      status: 'pending'
    }

    await submitVolunteer(applicationPayload)

    setFormSubmitted(true)
    setTimeout(() => {
      // Auto close with smooth slide down
      handleCloseApply()
      setTimeout(() => {
        setFormData({
          name: '', phone: '', email: '', address: '',
          experience: 'no', household: 'apartment', hoursAlone: '0-2'
        })
      }, 350)
    }, 3000)
  }

  const carouselRef = useRef(null)

  const scrollLeft = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: -340, behavior: 'smooth' })
    }
  }

  const scrollRight = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: 340, behavior: 'smooth' })
    }
  }

  const handleMouseDown = (e) => {
    if (!carouselRef.current) return
    setIsDown(true)
    setStartX(e.pageX - carouselRef.current.offsetLeft)
    setScrollLeftVal(carouselRef.current.scrollLeft)
    setIsDragging(false)
  }

  const handleMouseLeave = () => {
    setIsDown(false)
  }

  const handleMouseUp = () => {
    setIsDown(false)
  }

  const handleMouseMove = (e) => {
    if (!isDown || !carouselRef.current) return
    e.preventDefault()
    const x = e.pageX - carouselRef.current.offsetLeft
    const walk = (x - startX) * 1.5
    carouselRef.current.scrollLeft = scrollLeftVal - walk
    if (Math.abs(x - startX) > 10) {
      setIsDragging(true)
    }
  }


  return (
    <section id="adoption" className="relative flex flex-col justify-start select-none scroll-mt-12 w-full overflow-hidden bg-transparent">

      {/* 1. Clean Header Section */}
      <div className="relative pt-6 sm:pt-8 lg:pt-10 2xl:pt-12 pb-2 shrink-0 bg-transparent overflow-visible">
        <div className="relative z-10 max-w-8xl 2xl:max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 2xl:px-16 flex items-center justify-between text-left select-none w-full">
          {/* Header Content Container */}
          <div className="relative z-10 max-w-xl lg:max-w-2xl 2xl:max-w-3xl space-y-1 sm:space-y-1.5 text-left">
            <div className="flex items-center gap-1.5 text-brand-orange-700 font-bold text-[10px] sm:text-xs uppercase tracking-wider justify-start">
              <span>GIVE LOVE. GET LOYALTY.</span>
            </div>

            <h2 className="text-2xl sm:text-3.5xl lg:text-4xl 2xl:text-5xl font-display font-bold text-slate-900 leading-tight text-left tracking-tight">
              Find Your Perfect{' '}
              <span className="text-brand-orange-700 font-normal" style={{ fontFamily: "'Jumpswinter', sans-serif" }}>
                Companion.
              </span>
            </h2>

            <p className="text-xs sm:text-sm 2xl:text-base text-slate-600 max-w-md 2xl:max-w-xl leading-relaxed">
              Every pet deserves all the help. Choose to adopt and change a life—forever.
            </p>
          </div>

          {/* Absolute Positioned Massive Sketch Illustration - Balanced Position */}
          <div className="absolute -right-6 xs:-right-4 sm:right-2 lg:right-10 xl:right-16 hidden  md:block translate-y-3 xs:translate-y-4 sm:translate-y-6 md:translate-y-8 lg:translate-y-10 pointer-events-none select-none z-0">
            <img
              src="/sketch.png"
              alt="Pet companion sketch"
              className="w-64 h-64 xs:w-72 xs:h-72 sm:w-96 sm:h-96 md:w-[440px] md:h-[440px] lg:w-[540px] lg:h-[540px] xl:w-[620px] xl:h-[620px] 2xl:w-[700px] 2xl:h-[700px] object-contain opacity-85 sm:opacity-95"
            />
          </div>
        </div>
      </div>

      {/* 2. Carousel Container / Empty State */}
      <div className="relative z-10 w-full flex flex-col items-center justify-center overflow-hidden py-4 sm:py-6 lg:py-8 2xl:py-10 bg-transparent">
        {isLoading ? (
          <div className="py-12 flex items-center justify-center text-slate-400 text-sm font-semibold">
            Loading adoption profiles...
          </div>
        ) : pets.length > 0 ? (
          <>
            {/* The Carousel Track */}
            <div
              ref={carouselRef}
              onMouseDown={handleMouseDown}
              onMouseLeave={handleMouseLeave}
              onMouseUp={handleMouseUp}
              onMouseMove={handleMouseMove}
              className={`flex items-stretch gap-3.5 sm:gap-4.5 2xl:gap-6 overflow-x-auto scrollbar-none py-2 px-4 md:px-12 2xl:px-16 w-full ${
                isDown ? 'cursor-grabbing select-none scroll-auto' : 'cursor-grab scroll-smooth'
              }`}
            >
              {pets.map((pet, idx) => {
                return (
                  <div
                    key={pet.id || idx}
                    onClick={() => {
                      if (!isDragging) {
                        handleOpenApply(pet)
                      }
                    }}
                    className="relative group w-[220px] xs:w-[240px] sm:w-[260px] md:w-[280px] lg:w-[290px] 2xl:w-[320px] rounded-2xl bg-white p-3.5 sm:p-4 lg:p-4.5 2xl:p-5 border border-slate-200/80 shadow-xs hover:shadow-md transition-all duration-300 flex flex-col justify-between hover:-translate-y-1 cursor-pointer shrink-0 text-left overflow-hidden select-none"
                  >
                    {/* Pet Image Frame - Normal clean box */}
                    <div className="relative w-full h-[160px] xs:h-[180px] sm:h-[190px] lg:h-[200px] 2xl:h-[230px] rounded-xl overflow-hidden bg-brand-orange-50/50 mb-3 shrink-0">
                      <img
                        src={pet.image_url || pet.image}
                        alt={pet.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />

                      {/* Clean Top Tag */}
                      <div className="absolute top-2.5 left-2.5 px-2.5 py-0.5 bg-white/95 text-brand-orange-700 font-extrabold text-[9px] xs:text-[10px] uppercase tracking-wider shadow-xs border border-white/80 rounded-md">
                        {pet.tag || pet.breed || 'Rescue'}
                      </div>

                      {/* Hover Overlay */}
                      <div className="absolute inset-0 bg-brand-orange-700/85 backdrop-blur-2xs flex flex-col items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 p-3 text-center rounded-xl">
                        <span 
                          className="text-2xl sm:text-3xl text-white font-normal tracking-wider mb-1.5"
                          style={{ fontFamily: 'var(--app-font-heading)' }}
                        >
                          Adopt {pet.name}
                        </span>
                        <span className="text-[9px] xs:text-[10px] uppercase font-extrabold tracking-widest bg-white text-brand-orange-700 px-3 py-1 shadow-sm rounded-full">
                          View Profile &rarr;
                        </span>
                      </div>
                    </div>

                    {/* Bottom Content Bar */}
                    <div className="flex items-center justify-between pt-1.5 border-t border-slate-100 flex-grow text-left px-0.5">
                      <div>
                        <h3 className="text-sm sm:text-base 2xl:text-lg font-black font-display text-slate-800 leading-tight">
                          {pet.name}
                        </h3>
                        <span className="text-[10px] sm:text-[11px] text-slate-500 font-semibold mt-0.5 block">
                          {pet.breed} • {pet.age}
                        </span>
                      </div>

                      <span className="text-[11px] sm:text-xs font-bold text-brand-orange-700 uppercase tracking-wider flex items-center gap-1 group-hover:translate-x-1 transition-transform shrink-0">
                        Adopt &rarr;
                      </span>
                    </div>

                  </div>
                )
              })}
            </div>

            {/* Navigation Chevrons placed below the cards on the right side */}
            <div className="relative z-10 w-full max-w-8xl 2xl:max-w-8xl mx-auto px-4 md:px-12 2xl:px-16 hidden md:flex justify-end gap-2.5 pt-3">
              <button
                onClick={scrollLeft}
                className="w-11 h-11 min-w-[44px] min-h-[44px] bg-white hover:bg-slate-50 border border-slate-200 rounded-full flex items-center justify-center shadow-xs hover:scale-105 hover:text-brand-orange-700 transition-all cursor-pointer text-slate-700 focus:outline-none"
                aria-label="Previous adoption pet"
              >
                <svg className="w-4 h-4 stroke-current" fill="none" viewBox="0 0 24 24" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                </svg>
              </button>
              <button
                onClick={scrollRight}
                className="w-11 h-11 min-w-[44px] min-h-[44px] bg-white hover:bg-slate-50 border border-slate-200 rounded-full flex items-center justify-center shadow-xs hover:scale-105 hover:text-brand-orange-700 transition-all cursor-pointer text-slate-700 focus:outline-none"
                aria-label="Next adoption pet"
              >
                <svg className="w-4 h-4 stroke-current" fill="none" viewBox="0 0 24 24" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
              </button>
            </div>
          </>
        ) : (
          /* Empty State when no animals are available for adoption */
          <div className="w-full max-w-2xl mx-auto px-5 py-8 sm:py-12 text-center select-none bg-white border border-orange-200/80 rounded-3xl shadow-sm my-2">
            <div className="w-14 h-14 sm:w-18 sm:h-18 bg-brand-orange-50 text-brand-orange-600 rounded-full flex items-center justify-center mx-auto mb-3.5 border border-brand-orange-100 shadow-xs">
              <svg className="w-7 h-7 sm:w-9 sm:h-9 fill-current" viewBox="0 0 24 24">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
              </svg>
            </div>
            <span className="text-[10px] sm:text-xs uppercase font-extrabold tracking-widest text-brand-orange-700 block mb-1">
              ADOPTION STATUS
            </span>
            <h3 className="text-xl sm:text-2xl 2xl:text-3xl font-display font-bold text-slate-900 mb-2">
              All Rescued Animals Have Been Adopted!
            </h3>
            <p className="text-xs sm:text-sm text-slate-600 font-medium leading-relaxed max-w-md mx-auto mb-5">
              There are currently no animals awaiting adoption. Every rescued pet has found a loving family or is receiving dedicated care at our shelter.
            </p>
            {onOpenDonate && (
              <button
                onClick={onOpenDonate}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-orange-700 hover:bg-brand-orange-850 text-white font-extrabold text-xs sm:text-sm rounded-full shadow-md hover:shadow-lg transition-all cursor-pointer"
              >
                <span>Support New Rescues</span>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Adoption Application Modal (Bottom-Docked Wide Sliding) */}
      {isApplyModalOpen && selectedPet && (
        <div className="fixed inset-0 z-50 overflow-hidden flex items-end justify-center">
          {/* Backdrop overlay */}
          <div
            onClick={handleCloseApply}
            className={`fixed inset-0 bg-slate-900/60 backdrop-blur-xs transition-opacity duration-350 ease-in-out ${
              isModalAnimating ? 'opacity-100' : 'opacity-0'
            }`}
          />

          {/* Bottom-Docked Drawer Container */}
          <div
            data-lenis-prevent
            className={`relative w-full max-w-4xl lg:max-w-5xl 2xl:max-w-6xl bg-white rounded-none overflow-hidden shadow-2xl text-left flex flex-col max-h-[94vh] md:max-h-[88vh] z-10 transform transition-transform duration-350 ease-[cubic-bezier(0.16,1,0.3,1)] border-t-2 border-x border-slate-300 mb-0 ${
              isModalAnimating ? 'translate-y-0' : 'translate-y-full'
            }`}
          >
            {/* Modal Header */}
            <div className="bg-[#FAF6F2] border-b border-orange-200/80 px-4 py-3 sm:px-8 sm:py-4 flex items-center justify-between shrink-0 sticky top-0 z-30">
              <div className="space-y-0.5">
                <h3 className="text-base sm:text-2xl font-display font-bold text-slate-900 leading-snug">
                  Adoption Application: {selectedPet.name}
                </h3>
                <p className="text-[11px] sm:text-sm text-slate-600 font-medium">
                  Review pet profile and submit your adoption inquiry
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseApply}
                className="w-8 h-8 sm:w-9 sm:h-9 bg-slate-200/80 hover:bg-slate-300 text-slate-700 hover:text-slate-950 flex items-center justify-center font-bold text-sm sm:text-base transition-all cursor-pointer rounded-none border border-slate-300 shrink-0"
                title="Close"
              >
                ✕
              </button>
            </div>

            {/* Modal Body: Unified single scroll stream without big scrollbar */}
            <div className="flex flex-col md:flex-row overflow-y-auto no-scrollbar [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] flex-1 bg-white relative">
              {/* LEFT COLUMN: Pet Profile & Story */}
              <div className="w-full md:w-[38%] bg-slate-50 border-b md:border-b-0 md:border-r border-slate-200 p-3 sm:p-5 md:p-6 flex flex-col gap-3 sm:gap-4 select-none md:sticky md:top-0 md:self-start">
                {/* Pet Image Frame - Sticky on mobile */}
                <div className="sticky top-0 z-20 md:static w-full h-40 xs:h-48 sm:h-56 overflow-hidden relative bg-slate-200 rounded-none border border-slate-200 shadow-xs">
                  <img
                    src={selectedPet.image_url || selectedPet.image}
                    alt={selectedPet.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2.5 left-2.5 px-2.5 py-0.5 sm:px-3 sm:py-1 bg-brand-orange-600 text-white font-bold text-[9px] sm:text-[10px] uppercase tracking-wider rounded-none shadow-xs">
                    {selectedPet.tag || selectedPet.breed || 'Rescue'}
                  </div>
                </div>

                {/* Pet Details Grid */}
                <div className="space-y-2.5 sm:space-y-3 text-xs">
                  <div className="flex items-baseline justify-between">
                    <h4 className="text-xl sm:text-2xl font-bold text-slate-900 leading-none">{selectedPet.name}</h4>
                    <span className="text-xs font-bold text-brand-orange-700">{selectedPet.gender}</span>
                  </div>

                  <div className="grid grid-cols-2 gap-1.5 sm:gap-2 text-xs font-bold text-slate-700">
                    <div className="bg-white p-2 sm:p-2.5 border border-slate-200 rounded-none">
                      <span className="block text-[9px] uppercase tracking-wider text-slate-400 mb-0.5">Breed</span>
                      <span className="text-slate-900 font-extrabold truncate block">{selectedPet.breed}</span>
                    </div>
                    <div className="bg-white p-2 sm:p-2.5 border border-slate-200 rounded-none">
                      <span className="block text-[9px] uppercase tracking-wider text-slate-400 mb-0.5">Age</span>
                      <span className="text-slate-900 font-extrabold">{selectedPet.age}</span>
                    </div>
                    <div className="bg-white p-2 sm:p-2.5 border border-slate-200 rounded-none">
                      <span className="block text-[9px] uppercase tracking-wider text-slate-400 mb-0.5">Gender</span>
                      <span className="text-slate-900 font-extrabold">{selectedPet.gender}</span>
                    </div>
                    <div className="bg-white p-2 sm:p-2.5 border border-slate-200 rounded-none">
                      <span className="block text-[9px] uppercase tracking-wider text-slate-400 mb-0.5">Status</span>
                      <span className="text-brand-orange-700 font-extrabold flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-brand-orange-500 animate-pulse inline-block" />
                        {selectedPet.status}
                      </span>
                    </div>
                  </div>

                  {/* Companion Story */}
                  <div className="bg-orange-50/70 border border-orange-200 p-2.5 sm:p-3 text-[11px] sm:text-xs text-slate-700 leading-relaxed rounded-none">
                    <span className="block text-[9px] uppercase tracking-widest text-brand-orange-700 font-black mb-0.5">Companion Story</span>
                    "{selectedPet.story}"
                  </div>
                </div>
              </div>

              {/* RIGHT COLUMN: Inquiry Form */}
              <div className="w-full md:w-[62%] p-4 xs:p-5 sm:p-7 bg-white flex flex-col justify-between">
                <div>
                  {formSubmitted ? (
                    <div className="text-center py-10 space-y-4 max-w-md mx-auto">
                      <div className="w-14 h-14 bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto text-2xl font-bold border border-emerald-300 rounded-none">
                        ✓
                      </div>
                      <h4 className="text-2xl font-bold text-slate-900 leading-none">Application Submitted!</h4>
                      <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                        Thank you for applying to adopt <strong>{selectedPet.name}</strong>. Our adoption coordinators will call you back within 24–48 hours for home alignment check.
                      </p>
                      <button
                        type="button"
                        onClick={handleCloseApply}
                        className="px-8 py-3 bg-brand-orange-600 hover:bg-brand-orange-700 text-white font-bold text-xs uppercase tracking-wider transition-all shadow-sm cursor-pointer rounded-none"
                      >
                        Close Window
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-3.5 text-xs sm:text-sm">
                      {formError && (
                        <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs font-semibold rounded-none">
                          {formError}
                        </div>
                      )}

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="space-y-1 sm:col-span-1">
                          <label className="block text-xs font-bold text-slate-800">
                            Full Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="name"
                            required
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Your Name"
                            className="w-full px-3.5 py-2.5 text-xs sm:text-sm bg-slate-50 border border-slate-300 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-brand-orange-500 transition-all font-medium rounded-none"
                          />
                        </div>

                        <div className="space-y-1 sm:col-span-1">
                          <label className="block text-xs font-bold text-slate-800">
                            Phone Number <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            required
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="+91 98765 43210"
                            className="w-full px-3.5 py-2.5 text-xs sm:text-sm bg-slate-50 border border-slate-300 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-brand-orange-500 transition-all font-medium font-mono rounded-none"
                          />
                        </div>

                        <div className="space-y-1 sm:col-span-1">
                          <label className="block text-xs font-bold text-slate-800">
                            Email Address <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="email"
                            name="email"
                            required
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="name@email.com"
                            className="w-full px-3.5 py-2.5 text-xs sm:text-sm bg-slate-50 border border-slate-300 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-brand-orange-500 transition-all font-medium rounded-none"
                          />
                        </div>
                      </div>

                      <div className="space-y-1">
                        <label className="block text-xs font-bold text-slate-800">
                          Residential Address <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          name="address"
                          required
                          value={formData.address}
                          onChange={handleInputChange}
                          placeholder="Where will the pet stay? (Area, City, House Details)"
                          rows={2}
                          className="w-full px-3.5 py-2 text-xs sm:text-sm bg-slate-50 border border-slate-300 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-brand-orange-500 transition-all font-medium resize-none rounded-none"
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="block text-xs font-bold text-slate-800">Past Pet Experience</label>
                          <select
                            name="experience"
                            value={formData.experience}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2.5 text-xs sm:text-sm bg-slate-50 border border-slate-300 text-slate-900 focus:bg-white focus:outline-none focus:border-brand-orange-500 transition-all font-medium cursor-pointer rounded-none"
                          >
                            <option value="no">First time pet parent</option>
                            <option value="yes">Had pets before</option>
                            <option value="current">Currently have pets at home</option>
                          </select>
                        </div>

                        <div className="space-y-1">
                          <label className="block text-xs font-bold text-slate-800">Accommodation Type</label>
                          <select
                            name="household"
                            value={formData.household}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2.5 text-xs sm:text-sm bg-slate-50 border border-slate-300 text-slate-900 focus:bg-white focus:outline-none focus:border-brand-orange-500 transition-all font-medium cursor-pointer rounded-none"
                          >
                            <option value="apartment">Apartment / Flat</option>
                            <option value="house_yard">Independent House with Yard</option>
                            <option value="farm">Farm / Gated Villa Plot</option>
                          </select>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-200">
                        <button
                          type="button"
                          onClick={handleCloseApply}
                          className="px-5 py-2.5 text-xs font-bold text-slate-600 hover:text-slate-900 border border-slate-300 hover:bg-slate-100 transition-all cursor-pointer rounded-none"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-8 py-2.5 bg-brand-orange-600 hover:bg-brand-orange-700 text-white font-bold text-xs sm:text-sm shadow-md transition-all cursor-pointer flex items-center justify-center gap-2 rounded-none"
                        >
                          <span>Submit Adoption Application</span>
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </section>
  )
}

export default Adoption
